<template>
  <!-- eslint-disable vue/no-v-html -->
  <li :class="['cross-sell-item', { 'cross-sell-item--cart': cartPage }]">
    <a
      rel="nofollow"
      class="cross-sell-item__link modal-velociraptor-trigger"
      :title="$t('see_more.see_more_text')"
      @click.prevent="openModal"
    >
      <div class="cross-sell-item__thumbnail">
        <img
          class="img-responsive"
          :src="convertImagekit(src, 50)"
          :alt="product.name"
        />
      </div>
      <div class="cross-sell-item__content">
        <div class="cross-sell-item__title">
          {{ product.name }}
        </div>
        <div
          class="cross-sell-item__pricing cross-sell-item__pricing--promotion"
        >
          <span
            v-if="hasDiscount"
            ref="itemOldPrice"
            class="cross-sell-item__pricing--previous"
            >{{ formatPrice(oldPrice) }}</span
          >
          <span
            ref="itemPrice"
            class="cross-sell-item__pricing--current"
            itemprop="price"
            >{{ formatPrice(price) }}</span
          >
        </div>
        <!-- If there's customization -->
        <cross-sell-edit-btn
          v-if="customizations.length && !isPresent(persistedCustomValues)"
        />
        <template v-if="customizations.length || shouldChooseDevice">
          <div v-if="isPresent(persitedCaseDeviceId)">
            <span class="croll-sell-item__option-title"
              >{{ $t('device') }}:</span
            >
            <b>{{ displayCaseDevice }}</b>
          </div>
          <line-item-customizations
            :customizations-prop="displayCustomization"
            :show-customizations-prop="true"
          />
        </template>
      </div>
    </a>
    <checkbox-animated
      v-if="productPage"
      :checkbox-id="checkboxId"
      :selected="selected"
      @checked="toggleCheckbox"
    />
    <v-button
      v-else
      :label="$t('cross_sell.add')"
      class="btn btn--smallest cross-sell-item__add"
      :loading="isSubmiting"
      @click="saveCheckbox"
    />
    <modal
      ref="modal"
      :open="modalOpened"
      :content-class="'modal-velociraptor--cross-sell'"
      :show-footer="true"
      @close="closeModal"
    >
      <template slot="title">
        {{ $t('cross_sell.add_item') }}
      </template>
      <div
        class="modal-velociraptor__item modal-velociraptor__item--cross-sell"
      >
        <div class="modal-velociraptor__thumb position--relative">
          <dynamic-gallery-item
            v-if="customizations.length > 0"
            :sku="product.sku"
            :material="material"
            :custom-values="currentCustomizationValues"
            @load-start="isImageLoading = true"
            @load-end="isImageLoading = false"
          />
          <static-gallery-item
            v-else
            :src="
              typeof product.image === 'object'
                ? product.image.url
                : product.image
            "
            @load-start="isImageLoading = true"
            @load-end="isImageLoading = false"
          />
          <div v-show="isImageLoading">
            <transition name="fade">
              <bucket-loading />
            </transition>
          </div>
        </div>
        <div class="modal-velociraptor__text">
          <div class="modal-velociraptor__subtitle">{{ product.name }}</div>
          <div
            class="modal-velociraptor__pricing modal-velociraptor__pricing--promotion"
          >
            <span
              v-show="hasDiscount"
              ref="modalOldPrice"
              class="modal-velociraptor__pricing--previous"
              >{{ formatPrice(oldPrice) }}</span
            ><span
              ref="modalPrice"
              class="modal-velociraptor__pricing--current"
              >{{ formatPrice(price) }}</span
            >
          </div>
          <!-- Device -->
          <div
            v-if="devices.length > 0 && shouldChooseDevice"
            class="add-to-cart__casedevice section"
          >
            <div class="composite-device-selector">
              <device-brand-selector
                v-model="brand"
                :options="deviceBrands"
                :custom-input-name="`bundle_device_brand_${checkboxId}`"
                :custom-input-id="`bundle_device_brand_${checkboxId}`"
              />
              <device-selector
                v-model="device_id"
                :options="devices"
                option-value-key="id"
                :custom-input-name="`bundle_device_${checkboxId}`"
                :custom-input-id="`bundle_device_${checkboxId}`"
                required
              />
            </div>

            <case-type-selector
              v-if="isPresent(device_id)"
              :variant-id="variant_id || product.variant_id"
              :device-id="device_id"
              :product-id="product.id"
              :is-crossell="true"
              :checkbox-id="checkboxId"
              required
              :product-stock="productStock"
              @updateCaseTypeId="updateCaseTypeId"
              @updatePrice="updatePrice"
            />

            <case-device-input
              :id="`bundle_checkbox_case_device_${checkboxId}`"
              v-model="case_device_id"
              :device-id="device_id"
              :case-type-id="caseTypeId"
              :case-types="flatCaseTypes"
              :name="`bundle_checkbox_case_device[${checkboxId}]`"
            />
          </div>
          <!-- Customizations -->
          <div v-if="customizations.length > 0" class="subsection-title">
            <hr />
            <span>{{ $t('product_form.customize_product') }}</span>
          </div>
          <div
            v-for="custom in customizations"
            :key="custom.id"
            class="form__row"
          >
            <custom-input
              ref="customInput"
              v-model="customValues[custom.code]"
              :type="custom.customization_type"
              :custom="custom"
              :custom-input-name="`bundle_checkbox_customizations[${checkboxId}]`"
              :custom-input-id="`bundle_checkbox_${checkboxId}`"
              :out-of-stock="false"
            />
          </div>
          <div
            class="modal-velociraptor__description"
            v-html="product.description"
          ></div>
        </div>
      </div>
      <template slot="footer">
        <button
          :id="`add-cross-sell__item-${checkboxId}`"
          class="btn btn--success btn--large btn--block"
          @click.prevent="saveCheckbox"
        >
          {{ $t(buttonText) }}
        </button>
      </template>
    </modal>
  </li>
</template>

<script>
import convertImagekit from '@/utils/convertImagekit'
import priceable from '~/mixins/priceable'
import isPresent from '~/utils/isPresent'
import { PREVIEW_HOST } from '~/config'
import CrossSellEditBtn from '~/components/cross-sell/cross-sell-item/cross-sell-edit-btn/CrossSellEditBtn'
import CheckboxAnimated from '~/components/cross-sell/cross-sell-item/checkbox-animated/CheckboxAnimated'
import CustomInput from '~/components/product/form/custom-input/CustomInput'
import Modal from '~/components/Modal'
import BucketLoading from '~/components/loadings/bucket/BucketLoading'
import DynamicGalleryItem from '~/components/gallery/DynamicGalleryItem'
import StaticGalleryItem from '~/components/gallery/StaticGalleryItem'
import LineItemCustomizations from '~/components/line-item/line-item-customizations/LineItemCustomizations.vue'
import DeviceBrandSelector from '~/components/product/form/DeviceBrandSelector.vue'
import DeviceSelector from '~/components/product/form/device-selector'
import CaseTypeSelector from '~/components/product/form/case-type-selector/CaseTypeSelector.vue'
import CaseDeviceInput from '~/components/product/form/case-device-input'

import toQueryString from '~/utils/toQueryString'
import URLqueryStringToObject from '~/commun/utils/URLqueryStringToObject'
import withoutBlankValues from '~/commun/utils/withoutBlankValues'
import customizeable from '~/mixins/customizeable'
import deviceable from '~/mixins/deviceable'
import VButton from '~/components/v-button/VButton.vue'
export default {
  name: 'CrossSellItem',
  components: {
    CheckboxAnimated,
    CrossSellEditBtn,
    Modal,
    CustomInput,
    BucketLoading,
    DynamicGalleryItem,
    StaticGalleryItem,
    LineItemCustomizations,
    DeviceBrandSelector,
    DeviceSelector,
    CaseTypeSelector,
    VButton,
    CaseDeviceInput,
  },
  mixins: [priceable, customizeable, deviceable],
  props: {
    mode: {
      type: String,
      default: 'product',
    },
    checkbox: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isImageLoading: false,
      modalOpened: false,
      selected: false,
      persistedBrand: null,
      persistedDeviceId: null,
      persitedCaseDeviceId: null,
      persistedCustomValues: {},
      isSubmiting: false,
      loadedBundle: false,
      caseTypePrice: null,
      oldCaseTypePrice: null,
      productStock: null,
    }
  },
  computed: {
    cartPage() {
      return this.mode === 'cart'
    },
    productPage() {
      return this.mode === 'product'
    },
    hasDiscount() {
      return this.checkbox.has_discount
    },
    displayCaseDevice() {
      return this.device?.name
    },
    device() {
      return this.devices.find((device) => device.id === this.device_id)
    },
    caseType() {
      return this.caseTypes.find((ct) =>
        ct.devices.find((d) => d.device_id === this.device_id)
      )
    },
    caseDevice() {
      return this.caseType?.devices.find((d) => d.device_id === this.device_id)
    },
    buttonText() {
      return this.selected ? 'cross_sell.edit_item' : 'cross_sell.add_item'
    },
    product() {
      return this.checkbox.product
    },
    checkboxId() {
      return this.checkbox.checkbox_id
    },
    material() {
      // check case device
      return this.product.material
    },
    shouldChooseDevice() {
      // eslint-disable-next-line camelcase
      return this.checkbox?.choose_device
    },
    defaultCustom() {
      return URLqueryStringToObject(this.product.default_customization_params)
    },
    currentCustomizationValues() {
      return {
        ...this.defaultCustom,
        ...withoutBlankValues(this.customValues),
      }
    },
    additionalCosts() {
      const filledCustoms = this.customizations.filter((customization) =>
        isPresent(this.customValues[customization.code])
      )
      return filledCustoms
        .map((c) => Number(c.additional_cost) || 0)
        .reduce((acc, curr) => acc + curr, 0)
    },
    price() {
      const price =
        this.caseTypePrice ||
        (this.caseDevice ? this.caseDevice.checkbox_price : this.product.price)
      return Number(price) + Number(this.additionalCosts)
    },
    oldPrice() {
      const oldPrice =
        this.oldCaseTypePrice ||
        (this.caseDevice
          ? this.caseDevice.checkbox_old_price
          : this.product.old_price)
      return Number(oldPrice) + Number(this.additionalCosts)
    },
    displayCustomization() {
      return this.customizations
        .filter((customization) => {
          return isPresent(this.persistedCustomValues[customization.code])
        })
        .reduce((newObject, customization) => {
          newObject[customization.name] = this.persistedCustomValues[
            customization.code
          ]
          return newObject
        }, {})
    },
    src() {
      let imageUrl
      if (this.customizations.length > 0) {
        const customValues = {
          ...this.defaultCustom,
          ...withoutBlankValues(this.customValues),
        }
        const urlParams = toQueryString(customValues, {
          removeBlankKeys: true,
        }).toString()
        imageUrl = `${PREVIEW_HOST}/${this.product.sku}/${this.material}/mockup?${urlParams}`
      } else if (typeof this.product?.image === 'object') {
        imageUrl = this.product?.image?.url
      } else {
        imageUrl = this.product?.image
      }
      return imageUrl
    },
  },
  watch: {
    selected(newValue) {
      if (newValue && this.cartPage) {
        this.addToCart()
      }
    },
  },
  created() {
    this.brand = this.deviceBrands?.[0]
  },
  methods: {
    isPresent,
    updatePrice({ price, oldPrice }) {
      this.caseTypePrice = price
      this.oldCaseTypePrice = oldPrice
    },
    convertImagekit,
    updateCaseTypeId(ctId) {
      this.caseTypeId = ctId
    },
    async openModal() {
      if (!this.loadedBundle) {
        // this.resetValues()
        await this.setCheckboxProduct()
        this.brand = this.deviceBrands?.[0]
        this.validateCheckbox()
      }
      this.modalOpened = true
    },
    closeModal() {
      this.resetValues()
      this.modalOpened = false
    },
    async saveCheckbox() {
      if (!this.loadedBundle) {
        await this.setCheckboxProduct()
        this.brand = this.deviceBrands?.[0]
        this.validateCheckbox()
        this.openModal()

        return
      }

      if (this.validateCheckbox()) {
        this.persistedCustomValues = { ...this.customValues }
        this.persitedCaseDeviceId = this.case_device_id
        this.persistedBrand = this.brand
        this.persistedDeviceId = this.device_id
        this.selected = true
        this.closeModal()
        this.$emit('crossSellCheckboxChecked', {
          productNameCrossell: this.checkbox.product.name,
          productImageCrossSell: this.src,
          productPriceCrossSell: this.formatPrice(this.price),
          productOldPriceCrossSell: this.formatPrice(this.oldPrice),
          productDisplayCaseDevice: this.displayCaseDevice,
        })
      } else this.openModal()
    },
    async setCheckboxProduct() {
      this.isSubmiting = true
      const storeInfo = this.$store.getters.storeInfo
      const params = {
        ...storeInfo,
        case_device: this.caseDevice,
      }
      const endpoint = `bundle/${this.checkboxId}`

      const { product } = await this.$axios.$get(endpoint, {
        params,
      })

      const productStock = await this.$axios.$get(
        `/products/${product.slug}/stock`,
        {
          params,
        }
      )

      this.productStock = productStock

      this.checkbox.product = product
      this.loadedBundle = true
      this.isSubmiting = false
    },
    uncheckCheckbox() {
      if (this.selected) this.selected = false
      this.persistedCustomValues = {}
      this.persitedCaseDeviceId = null
      this.persistedBrand = null
      this.persistedDeviceId = null
      this.$emit('crossSellCheckboxUnchecked', this.checkbox)
    },
    toggleCheckbox() {
      this.selected ? this.uncheckCheckbox() : this.saveCheckbox()
    },
    validateCheckbox() {
      const isValidatable = (input) => typeof input.validate === 'function'
      const isValid = (validation) => validation.hasErrors
      const inputValidations = this.$refs.modal.$children
        .filter(isValidatable)
        .map((input) => input.validate())
      return !inputValidations.some(isValid)
    },
    resetValues() {
      this.customValues = { ...this.persistedCustomValues }
      this.case_device_id = this.persitedCaseDeviceId
      this.device_id = this.persistedDeviceId
      if (this.persistedBrand) this.brand = this.persistedBrand
    },
    addToCart() {
      this.isSubmiting = true

      const bundleCheckboxCaseDevice = {}
      bundleCheckboxCaseDevice[this.checkboxId] = this.persitedCaseDeviceId

      const customizations = {}
      this.customizations
        .filter((customization) => {
          return isPresent(this.persistedCustomValues[customization.code])
        })
        .forEach((customization) => {
          customizations[customization.id] = this.persistedCustomValues[
            customization.code
          ]
        })

      const storeInfo = this.$store.getters.storeInfo
      const params = {
        checkbox_id: this.checkboxId,
        order_id: this.orderNumber,
        bundle_checkbox_case_device: bundleCheckboxCaseDevice,
        bundle_customizations: customizations,
        ...storeInfo,
      }
      return this.$apiv2
        .$post('carts/populate_bundle', params)
        .then(this.parseErrorResponse)
        .then(this.setOrderAttrs)
        .catch(this.onError)
        .finally(this.endSubmit)
    },
    parseErrorResponse(data) {
      if (data?.error) throw data.error

      return data
    },
    setOrderAttrs(cart) {
      if (!isPresent(cart)) return

      const { cartQty } = this.$store.state
      this.$store.commit('SET_CART_QTY', cartQty + 1)
      this.$emit('updateCart', { cart, id: this.checkboxId })
    },
    onError(msg) {
      this.$displayTopMessage(this.$t('cross_sell.cart_error'), {
        type: 'error',
      })
      this.selected = false
      this.$emit('error', msg)
    },
    endSubmit() {
      this.isSubmiting = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/btn.scss';

.cross-sell-item {
  &:last-child {
    border-bottom-width: 0;
  }

  &.cross-sell-item--cart {
    padding: 15px;
    border-top: 1px solid #e6e6ea;
    border-bottom: 0;
    background-color: #f6f6fa;

    .cross-sell-item__link {
      width: calc(100% - 128px);
    }

    .cross-sell-item__thumbnail {
      flex-shrink: 0;
      width: 90px;
    }
    .cross-sell-item__content {
      width: calc(100% - 75px);
      margin-left: 15px;
    }
    .cross-sell-item__title,
    .cross-sell-item__pricing {
      font-size: 13px;
      color: #555;
    }
    .cross-sell-item__title {
      margin-bottom: 5px;
    }

    .cross-sell-item__pricing {
      margin: 5px 0;
    }

    .cross-sell-item__edit {
      font-size: 12px;
      padding: 0 10px;
      height: 36px;
      line-height: 36px;
      border-color: #e6e6ea;
      border-radius: 4px;

      &:hover {
        background: #eee;
      }

      .lnr-pencil {
        margin-right: 2px;
      }
    }

    .cross-sell-item__add {
      align-self: auto;
      min-width: 55px;
    }
  }

  list-style: none;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid $color_gray;

  .croll-sell-item__option-title {
    display: inline-block;
    margin-top: 2px;
  }

  .cross-sell-item__link {
    display: flex;
    width: calc(100% - 50px);
    text-decoration: none;
  }

  .cross-sell-item__thumbnail {
    width: 50px;

    img {
      display: block;
    }
  }

  .cross-sell-item__content {
    width: calc(100% - 70px);
    margin-left: 10px;
  }

  .cross-sell-item__title,
  .cross-sell-item__pricing {
    display: block;
    font-size: 12px;
    color: #777;
    text-transform: uppercase;
    white-space: nowrap;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    line-height: 16px;
  }

  .cross-sell-item__pricing--previous {
    text-decoration: line-through;
    margin-right: 5px;
  }

  .cross-sell-item__pricing--current {
    color: $v4_color_primary;
  }

  .cross-sell-item__add {
    align-self: center;
  }

  .cross-sell-item__option {
    color: #333;
    margin-bottom: 0px;
    margin-top: 0px;
  }
}

@media (min-width: $screen_tablet) {
  .cross-sell-item {
    &.cross-sell-item--cart {
      margin: 0;
      .cross-sell-item__thumbnail {
        width: 100px;
      }
      .cross-sell-item__content {
        width: calc(100% - 85px);
      }
    }
  }
}
</style>
